import type { UpdateContactInput } from 'app/generated/gql/graphql'
import type { List } from 'app/generated/graphql'
import { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment-timezone'
import { assigneeInput } from 'lib/taskAssignee'

import { graphql } from 'app/generated/gql'
import { CONTACT_FIELDS } from 'lib/contactFieldConfig'
import type { ContactJson, LocationJson } from 'app/types/entities'
import { SEND_SMS_MUTATION } from 'app/components/contacts/inbox/graphql'
import { CONTACT_THREAD_IDS_QUERY, SMS_AGENT_READY_QUERY, THREADS_AGENT_STATE_QUERY } from 'app/graphql/agentThreads'
import { HELD_MESSAGES_QUERY } from 'app/graphql/scheduledMessages'
import {
  ACTION_CENTER_FLAG,
  isAssignablePipeline,
  isMembershipBacked,
  isSystemPipelineId,
  systemQueueAdmits,
} from 'app/components/action-center/systemPipelines'
import {
  NON_PATIENT_ACTION_ID,
  PIPELINE_ACTION_ID,
  REMOVE_FROM_LIST_ACTION_ID,
  SMS_ACTION_ID,
  TAGS_ACTION_ID,
  TASK_ACTION_ID,
  WORKFLOW_ACTION_ID,
} from './bulkActionIds'
import { parseJsonField } from 'app/lib/workflows/deserialize'
import { LIST_WORKFLOWS_QUERY, TRIGGER_WORKFLOW_ON_CONTACT_MUTATION } from 'app/lib/workflows/graphql'
import { isManualEnrollmentTrigger } from 'app/lib/workflows/serialize'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { gqlFetchOne, gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import { substituteTokens, TEMPLATE_TOKENS } from 'app/lib/messageTemplates'
import { isSchedulable, resolveSendAt } from 'app/lib/scheduledSend'
import { isQuietHours } from 'app/lib/smsQuietHours'
import { classifySendSmsFailure } from 'app/lib/smsSendErrors'
import {
  useContactStore,
  useListMembershipStore,
  useListStore,
  usePipelineStore,
  useTaskStore,
} from 'app/stores/StoreContext'
import { useUser, useWorkspace } from 'app/utils'
import { isCompanionPipeline } from 'lib/companionPipelines'
import { isSMCOrg } from 'lib/isSMCOrg'
import { isTrackableOrg } from 'lib/isTrackableOrg'
import { practiceListCatalog, stageIdForName } from 'lib/listMemberships'
import { isFilterList } from 'lib/practiceLists'
import { logError } from 'lib/logging'
import type {
  BulkAction,
  BulkActionOption,
  BulkActionValue,
  BulkActionsConfig,
  BulkProgressReporter,
  BulkRowOutcome,
  PipelineOption,
  PipelineValue,
  SmsValue,
  TaskValue,
} from 'app/components/table/bulkActions/types'

const CREATE_TAG_COLOR = graphql(`
  mutation BulkCreateTagColor($input: CreateTagColorInput!) {
    createTagColor(input: $input) {
      id
    }
  }
`)

// The session's workspace scope lives in ONE browser-global cookie, so another
// tab can silently re-scope it between the page load and an apply run, after
// which every row fails with a raw "Contact not found". Same operation the
// AuthStore uses; re-asserted here right before writing.
const SWITCH_WORKSPACE_MUTATION = graphql(`
  mutation SwitchWorkspace($targetWorkspaceId: String) {
    switchWorkspace(targetWorkspaceId: $targetWorkspaceId) {
      accessToken
    }
  }
`)

// Workflows offered for bulk enrolment: ACTIVE manual-enrollment workflows (TRK-5430) and nothing
// else. They're built for this — nothing enters them except a push — so a bulk enrolment can't
// collide with the workflow's own trigger and message someone twice. The engine's bulk API
// (TRK-5383) will replace the per-contact loop; the picker contract stays the same.

/** Option sets for select-type fields (mirrors the inline drawer editors). */
const SELECT_OPTIONS: Record<string, BulkAction[`options`]> = {
  leadStatus: [
    { label: `Needs Action`, value: `Needs Action` },
    { label: `In Progress`, value: `In Progress` },
    { label: `Scheduled`, value: `Scheduled` },
    { label: `Closed Lost`, value: `Closed Lost` },
  ],
}

/**
 * Fills SMS merge tokens with each contact's values via the shared template
 * lib (canonical keys; legacy flat keys resolve through its alias map). When a
 * value is empty the configured fallback is used. Unknown/unresolvable tokens
 * are **stripped** rather than passed through — this is a real outbound SMS,
 * so a typo'd `{{whatevr}}` must never be delivered as literal text. Exported
 * for unit tests.
 */
export function personalizeSms(text: string, contact: ContactJson, fallbacks?: Record<string, string>): string {
  return substituteTokens(text, { contact }, { fallbacks }).text
}

// A bulk selection can be large; look up every already-queued contact in one
// query rather than one per row.
const HELD_CONTACT_IDS_LIMIT = 1000

/** Shown when the already-queued lookup below could not run at all, so the run went ahead without that check. */
export const ALREADY_QUEUED_CHECK_FAILED_CAVEAT = `The already-scheduled check couldn't run, so some contacts may now have more than one message queued.`

type HeldContactIdsResult = { ids: Set<string>; failed: boolean }

/**
 * Contact ids in the selection that already have a pending held message, in
 * ONE query for the whole run. The scheduled-SMS branch below skips these: the
 * thread composer enforces one queued message per contact, and a bulk run
 * should not quietly create a second. A lookup failure fails open (`ids` comes
 * back empty so nobody is wrongly skipped) but is flagged via `failed`, so the
 * caller can tell the user the check didn't run rather than silently letting
 * a contact end up with two queued messages.
 */
async function heldContactIds(rows: ContactJson[]): Promise<HeldContactIdsResult> {
  const ids = [...new Set(rows.map((row) => row.id).filter((id): id is string => !!id))]
  if (!ids.length) return { ids: new Set(), failed: false }
  try {
    const result = await gqlQuery<{ messages?: { hits?: ({ contactId?: string | null } | null)[] | null } }>(
      HELD_MESSAGES_QUERY,
      {
        search: `contactId:(${ids.map((id) => `"${id}"`).join(` OR `)}) AND status:pending`,
        pagination: { limit: HELD_CONTACT_IDS_LIMIT },
      },
    )
    if (result.error) {
      logError(`Could not check already-queued messages for bulk SMS scheduling`, result.error.message)
      return { ids: new Set(), failed: true }
    }
    const hits = result.data?.messages?.hits ?? []
    return { ids: new Set(hits.map((hit) => hit?.contactId).filter((id): id is string => !!id)), failed: false }
  } catch (err) {
    logError(`Could not check already-queued messages for bulk SMS scheduling`, err)
    return { ids: new Set(), failed: true }
  }
}

/** Shown when the agent lookup below could not run, so the run went ahead without that check. */
export const AGENT_CHECK_FAILED_CAVEAT = `The AI Agent check couldn't run, so some messages may be queued on threads the agent is answering.`

/**
 * Contact ids in the selection whose thread the AI agent is answering.
 *
 * A Thread carries no contact reference, so this is two hops: the selection's
 * messages give `threadId`, and the threads give `aiEnabled`. Locations without
 * a ready agent are dropped first, because there the thread flag cannot make
 * this true and most runs stop here with no further query.
 *
 * Best effort by design, and the same shape as `heldContactIds` above. A failure
 * fails open and is flagged rather than cancelling the run: the collision this
 * prevents is a nuisance, not a compliance breach, and the dispatcher is the
 * authoritative check at fire time (TRK-5939). The message page below is capped,
 * so a selection whose contacts carry more messages than fit can leave a thread
 * unseen; that is the same cap the already-queued check accepts.
 */
async function agentHandledContactIds(rows: ContactJson[]): Promise<HeldContactIdsResult> {
  const ids = [...new Set(rows.map((row) => row.id).filter((id): id is string => !!id))]
  const locationIds = [...new Set(rows.map((row) => row.locationId).filter((id): id is string => !!id))]
  if (!ids.length || !locationIds.length) return { ids: new Set(), failed: false }
  try {
    const ready = await Promise.all(
      locationIds.map(async (locationId) => {
        const result = await gqlQuery<{ smsAgentReady?: boolean | null }>(
          SMS_AGENT_READY_QUERY,
          { locationId },
          { client: opsClient },
        )
        if (result.error) throw new Error(result.error.message)
        return result.data?.smsAgentReady === true ? locationId : undefined
      }),
    )
    const readyLocations = new Set(ready.filter((id): id is string => !!id))
    if (!readyLocations.size) return { ids: new Set(), failed: false }

    const quoted = (values: string[]) => values.map((value) => `"${value}"`).join(` OR `)
    const scoped = rows.filter((row) => row.id && row.locationId && readyLocations.has(row.locationId))
    if (!scoped.length) return { ids: new Set(), failed: false }

    const messages = await gqlQuery<{
      messages?: { hits?: ({ contactId?: string | null; threadId?: string | null } | null)[] | null }
    }>(CONTACT_THREAD_IDS_QUERY, {
      search: `contactId:(${quoted(scoped.map((row) => row.id!))}) AND threadId:*`,
      pagination: { limit: HELD_CONTACT_IDS_LIMIT },
    })
    if (messages.error) throw new Error(messages.error.message)

    const threadToContacts = new Map<string, Set<string>>()
    for (const hit of messages.data?.messages?.hits ?? []) {
      if (!hit?.threadId || !hit.contactId) continue
      const contacts = threadToContacts.get(hit.threadId) ?? new Set<string>()
      contacts.add(hit.contactId)
      threadToContacts.set(hit.threadId, contacts)
    }
    if (!threadToContacts.size) return { ids: new Set(), failed: false }

    const threads = await gqlQuery<{
      threads?: { hits?: ({ id?: string | null; aiEnabled?: boolean | null } | null)[] | null }
    }>(THREADS_AGENT_STATE_QUERY, {
      search: `id:(${quoted([...threadToContacts.keys()])})`,
      pagination: { limit: HELD_CONTACT_IDS_LIMIT },
    })
    if (threads.error) throw new Error(threads.error.message)

    const handled = new Set<string>()
    for (const thread of threads.data?.threads?.hits ?? []) {
      if (!thread?.id || thread.aiEnabled !== true) continue
      for (const contactId of threadToContacts.get(thread.id) ?? []) handled.add(contactId)
    }
    return { ids: handled, failed: false }
  } catch (err) {
    logError(`Could not check AI Agent threads for bulk SMS scheduling`, err)
    return { ids: new Set(), failed: true }
  }
}

export type WorkflowHit = { id: string; name?: string | null; active?: boolean | null; triggerJson?: string | null }
type StoredTrigger = { event?: string; filters?: { attribute: string; value?: string | null }[] }

/**
 * Which workflows the "Enroll in workflow" action offers: ACTIVE manual-enrollment workflows only
 * (TRK-5430). A workflow that also fires on its own trigger could enroll the same contact twice
 * (once from the push, once from the event it happens to match) and message them twice, so those
 * stay out of the bulk picker entirely — manual-only workflows can't double-enroll by construction.
 * Exported for unit tests.
 */
export function workflowEnrollmentOptions(hits: WorkflowHit[]): BulkActionOption[] {
  return hits
    .map((workflow) => ({ workflow, trigger: parseJsonField<StoredTrigger>(workflow.triggerJson) }))
    .filter(({ workflow, trigger }) => !!workflow.active && isManualEnrollmentTrigger(trigger ?? {}))
    .map(({ workflow }) => ({ label: workflow.name || `Untitled workflow`, value: workflow.id }))
}

/**
 * Why a contact can't be bulk-marked non-patient, or `null` when it's eligible.
 *
 * The patient-linked test is `patientIds` alone — stricter than the drawer's
 * `isIntegrated && patientIds`, since a bulk selection spans locations and per-row
 * integration state isn't in the row data. For an action that strips lifecycle and lead
 * status, over-skipping is the right side to err on; the drawer still handles those
 * contacts one at a time with the location check available. Exported for unit tests.
 */
export function nonPatientSkipReason(contact: ContactJson): string | null {
  if ((contact.patientIds?.length ?? 0) > 0) return `Linked to a patient record`
  if (contact.patientStatus === `NonPatient`) return `Already non-patient`
  return null
}

/**
 * Builds the contacts bulk-action set from the editable-field config plus tags,
 * and applies changes via the interim client-side adapter (looped updateContact).
 * Swapping `apply` to a single `bulkUpdateContacts` mutation is the API handoff.
 *
 * `removeFromList` is the list a table shows the members of. Only a manual list offers Remove
 * from list: a filter list's members are there because of a property on the contact, and a
 * system list's by its own rule, so taking one off would not hold.
 */
export function useContactBulkActions(options: { removeFromList?: List } = {}): BulkActionsConfig<ContactJson> {
  const removableList =
    options.removeFromList && !isFilterList(options.removeFromList) && !options.removeFromList.system
      ? options.removeFromList
      : undefined
  const contactStore = useContactStore()
  const pipelineStore = usePipelineStore()
  const listStore = useListStore()
  const membershipStore = useListMembershipStore()
  const taskStore = useTaskStore()
  const user = useUser()
  const workspace = useWorkspace()
  const currentWorkspaceId = workspace?.id
  const checkPermission = useCheckPermission()
  // Bulk Send SMS is a real outward action; keep it behind a toggle until enabled.
  const bulkSmsEnabled = useFeatureToggle(`bulk-sms`)
  // System boards only exist for workspaces that have the Action Center.
  const actionCenterEnabled = useFeatureToggle(ACTION_CENTER_FLAG)
  const listsOn = useFeatureToggle(`grouped-navigation`)
  const copy = useListCopy()
  const [pipelineOptions, setPipelineOptions] = useState<PipelineOption[]>([])
  const [workflowOptions, setWorkflowOptions] = useState<BulkActionOption[]>([])

  // Mirror the inline-editor permission gates so a set-value action only appears
  // when the user can actually edit that field (see ContactSpotlight / table columns).
  const isSuper = user?.type === `super`
  const isSMC = isSMCOrg(user?.orgId ?? ``)
  const canEditMarketing = isSuper || checkPermission(`org:marketing:advanced-filters`)
  const canEditLeadStatus = checkPermission([`org:contacts:edit-lead-status`, `contacts:lead-status`])
  const canEditUtm = !isSMC && (isSuper || checkPermission(`org:contacts:see-utm`))
  // Mark as non-patient is destructive and internal-facing (it hides contacts from
  // dashboards and deletes lifecycle/lead status), so it carries two gates: the Trackable
  // org lock, plus the same per-user permission the drawer's kebab menu enforces.
  const canMarkNonPatient =
    isTrackableOrg(user?.orgId ?? undefined) &&
    checkPermission([`org:contacts:set-non-patient`, `contacts:mark-non-patient`])

  useEffect(() => {
    // With Action Center on, the full list including the system boards, rather than
    // `assignableHits`. This action picks one pipeline for a whole selection, so
    // there is no single contact to test at option-build time; the per-row check
    // happens at apply time instead (see PIPELINE_ACTION_ID below), where each row
    // can be admitted or refused on its own. `system:leads` still drops out: its
    // board moves `leadStatus`, so a membership written there would do nothing.
    //
    // Gated the same way `ContactPipelinesCell` gates its picker. This hook serves
    // the general Contacts page, and a workspace without Action Center has no board
    // to work the membership on, so offering one there would write a membership
    // nobody in that workspace could ever see or clear.
    //
    // With the lists on, a list is what a person adds to: only the system boards stay on the
    // pipeline mutation, and the practice's own lists come from the list store.
    Promise.all([pipelineStore.fetchList(), listsOn && !listStore.list ? listStore.fetchList() : undefined])
      .then(() => {
        const offerable = actionCenterEnabled
          ? pipelineStore.hits.filter(isMembershipBacked)
          : pipelineStore.hits.filter((pipeline) => isAssignablePipeline(pipeline))
        const pipelineChoices = offerable
          .filter((pipeline) => !isCompanionPipeline(pipeline))
          .filter((pipeline) => !listsOn || isSystemPipelineId(pipeline.id))
          .map((p) => ({
            id: p.id,
            name: p.name,
            stages: [...(p.stages ?? [])].sort((a, b) => a.order - b.order).map((s) => s.name),
          }))
          .filter((p) => p.stages.length > 0)
        const listChoices = listsOn
          ? practiceListCatalog(listStore.hits.filter((list) => !isFilterList(list))).map((list) => ({
              id: list.id,
              name: list.name,
              stages: list.stages.map((s) => s.name),
            }))
          : []
        setPipelineOptions([...pipelineChoices, ...listChoices])
      })
      .catch((err) => logError(`Failed to load pipelines for bulk actions`, err))
  }, [pipelineStore, listStore, listsOn, actionCenterEnabled])

  useEffect(() => {
    gqlQuery<{ workflows: { hits: WorkflowHit[] } }>(LIST_WORKFLOWS_QUERY, {
      pagination: { limit: 100 },
      sort: { field: `updatedDate`, direction: `DESC` },
    })
      .then((res) => setWorkflowOptions(workflowEnrollmentOptions(res.data?.workflows.hits ?? [])))
      .catch((err) => logError(`Failed to load workflows for bulk actions`, err))
  }, [])

  const actions = useMemo<BulkAction[]>(() => {
    const canEditField = (editFieldName: string): boolean => {
      switch (editFieldName) {
        case `leadStatus`:
          return canEditLeadStatus
        case `utmSource`:
        case `utmMedium`:
        case `utmCampaign`:
          return canEditUtm
        case `reportedAttribution`:
          return canEditMarketing
        default:
          return true
      }
    }

    // Tags is the hero action and isn't part of CONTACT_FIELDS, so add it first.
    const tagsAction: BulkAction = {
      id: TAGS_ACTION_ID,
      label: `Add tags`,
      control: `tags`,
      placeholder: `Search or create a tag…`,
      // Persist a brand-new tag (name + color) — addTags only stamps the string on
      // each contact; the tag itself must be created like the inline flow does.
      // Fire-and-forget: it runs before the per-contact addTags loop, so if this
      // TagColor lands late or fails the tag still attaches — it just renders
      // colorless until the color exists. The race is intentional and self-healing.
      onCreateTag: (name, color) => {
        gqlMutation(CREATE_TAG_COLOR, {
          input: { entity: `Contact`, name, color },
        })
          .then((res) => {
            if (res.error) logError(`Failed to create tag color: ${name}`, res.error)
          })
          .catch((err) => logError(`Failed to create tag color: ${name}`, err))
      },
    }

    const pipelineAction: BulkAction = {
      id: PIPELINE_ACTION_ID,
      label: copy.addToList,
      control: `pipeline`,
      pipelineOptions,
    }

    const taskAction: BulkAction = {
      id: TASK_ACTION_ID,
      label: `Create task`,
      control: `task`,
      placeholder: `Task title`,
    }

    // Enroll the selection into an active workflow (the segment side of database-reactivation).
    // Manual-enrollment workflows are the natural targets and lead the list (TRK-5430).
    const workflowAction: BulkAction = {
      id: WORKFLOW_ACTION_ID,
      label: `Enroll in workflow`,
      control: `select`,
      options: workflowOptions,
      placeholder: `Choose a workflow…`,
    }

    const smsAction: BulkAction = {
      id: SMS_ACTION_ID,
      label: `Send SMS`,
      control: `sms`,
      // One catalog everywhere: the same tokens the template editor offers.
      variables: TEMPLATE_TOKENS.map((t) => ({ key: t.key, token: `{{${t.key}}}`, label: t.label })),
    }

    // Trackable-only cleanup action. Copy mirrors the drawer's confirm, pluralized.
    const nonPatientAction: BulkAction = {
      id: NON_PATIENT_ACTION_ID,
      label: `Mark as non-patient`,
      control: `confirm`,
      confirmTitle: `Mark as Non-Patient`,
      confirmDescription: (count) =>
        `Marking ${count} ${count === 1 ? `contact` : `contacts`} will prevent them from being shown on dashboards and in calculations as well as delete crucial data including lifecycle and lead status.\n\nContacts linked to a patient record, and contacts already marked non-patient, are skipped.\n\nThis functionality SHOULD NOT be used to close or disqualify a lead.`,
    }

    const fieldActions = CONTACT_FIELDS.filter(
      (field) =>
        field.isEditable &&
        field.editFieldName &&
        // leadOwner needs the async staff picker — deferred to a follow-up.
        field.editFieldName !== `leadOwner` &&
        // channel is only editable for SMC orgs with permission — excluded from bulk.
        field.editFieldName !== `channel` &&
        // only show fields the user can actually edit (mirrors the inline editors).
        canEditField(field.editFieldName),
    ).map<BulkAction>((field) => {
      const options = SELECT_OPTIONS[field.editFieldName!]
      return {
        id: field.editFieldName!,
        label: `Set ${field.label.toLowerCase()}`,
        control: options ? `select` : `text`,
        options,
      }
    })

    // No dialog: a removal from a manual list is undone by adding them back, as on the board.
    const removeFromListAction: BulkAction = {
      id: REMOVE_FROM_LIST_ACTION_ID,
      label: copy.removeFromList,
      control: `none`,
    }

    return [
      tagsAction,
      pipelineAction,
      ...(removableList ? [removeFromListAction] : []),
      taskAction,
      // Only offered when an active contact-subject workflow exists to enroll into.
      ...(workflowOptions.length ? [workflowAction] : []),
      // Send SMS stays behind the `bulk-sms` feature toggle.
      ...(bulkSmsEnabled ? [smsAction] : []),
      // Locked to the Trackable org (and the mark-non-patient permission).
      ...(canMarkNonPatient ? [nonPatientAction] : []),
      ...fieldActions,
    ]
  }, [
    copy,
    pipelineOptions,
    workflowOptions,
    canEditLeadStatus,
    canEditUtm,
    canEditMarketing,
    bulkSmsEnabled,
    canMarkNonPatient,
    removableList,
  ])

  const apply = useCallback(
    async (actionId: string, value: BulkActionValue, rows: ContactJson[], onProgress?: BulkProgressReporter) => {
      // Pin the session to this view's workspace before any write runs. Fail-closed:
      // if the pin can't be confirmed, report the run as failed rather than writing
      // into whatever workspace the cookie currently points at.
      if (currentWorkspaceId) {
        const failRun = (reason: string) => ({
          outcomes: rows.map((row) => ({
            id: row.id ?? ``,
            label: row.name || row.id || `Unknown contact`,
            ok: false,
            reason,
          })),
        })
        try {
          const scope = await gqlMutation(SWITCH_WORKSPACE_MUTATION, {
            targetWorkspaceId: currentWorkspaceId,
          })
          if (scope.error) {
            logError(`Bulk apply failed to pin workspace scope`, scope.error.message)
            return failRun(`Couldn't confirm the workspace. Nothing was changed; try again.`)
          }
        } catch (err) {
          logError(`Bulk apply failed to pin workspace scope`, err)
          return failRun(`Couldn't confirm the workspace. Nothing was changed; try again.`)
        }
      }

      // A scheduled send holds one message per contact, each instant resolved on
      // that CONTACT'S OWN practice clock: the API stores an absolute instant, but
      // the user picked a wall clock, and "8:00 AM" for a run spanning New York and
      // Los Angeles is two different instants. Resolving per contact is what makes
      // multi-timezone bulk scheduling correct at all; a shared instant would text
      // one of those practices in the middle of the night.
      //
      // Per-recipient eligibility (opt-out, quiet hours, missing phone) is
      // re-checked by the dispatcher at fire time and stays authoritative, so
      // nothing is filtered out of the selection on those grounds here. Two
      // deliberate exceptions, both about the time rather than the recipient.
      // First, a contact that already has a held message: the thread composer
      // enforces one queued message per contact, and a bulk run must not quietly
      // create a second, so that set is looked up once for the whole selection
      // and skipped. Second, a contact whose own practice clock rejects the
      // picked time, which the loop below checks per row.
      if (actionId === SMS_ACTION_ID) {
        const smsValue = value as SmsValue
        if (smsValue.sendAtLocal) {
          const sendAtLocal = smsValue.sendAtLocal
          // One instant for the whole run's validity checks. A four hundred row
          // loop can straddle a minute boundary, and two contacts at the same
          // practice must not get different answers to the same question.
          const runStartedAt = new Date()
          const { ids: queued, failed: heldCheckFailed } = await heldContactIds(rows)
          const { ids: agentThreads, failed: agentCheckFailed } = await agentHandledContactIds(rows)
          const timezoneCache = new Map<string, string | undefined>()
          const resolveLocationTimezone = async (locationId?: string | null): Promise<string | undefined> => {
            if (!locationId) return undefined
            if (!timezoneCache.has(locationId)) {
              try {
                const location = await gqlFetchOne<LocationJson>(`location`, locationId)
                timezoneCache.set(locationId, location?.timezone ?? undefined)
              } catch (err) {
                logError(`Bulk scheduled SMS could not load location ${locationId}`, err)
                timezoneCache.set(locationId, undefined)
              }
            }
            return timezoneCache.get(locationId)
          }

          const outcomes: BulkRowOutcome[] = []
          const tally = { ok: 0, failed: 0, skipped: 0 }
          const record = (outcome: BulkRowOutcome) => {
            outcomes.push(outcome)
            if (outcome.ok) tally.ok += 1
            else if (outcome.skipped) tally.skipped += 1
            else tally.failed += 1
            onProgress?.({ done: outcomes.length, total: rows.length, ...tally, last: outcome })
          }

          for (const row of rows) {
            const label = row.name || row.id || `Unknown contact`
            if (!row.id) {
              record({ id: ``, label, ok: false, reason: `Contact is missing an id` })
              continue
            }
            // Separate from the timezone check below, which a contact with no
            // location also fails: reporting that the location has no timezone
            // when the contact has no location reads as a setting someone can go
            // and fix.
            if (!row.locationId) {
              record({ id: row.id, label, ok: false, skipped: true, reason: `Contact is not at a location` })
              continue
            }
            // A presence check is not a validity check: moment-timezone resolves
            // an unrecognized zone name to UTC with `isValid()` still true, so a
            // bogus timezone would silently schedule on the wrong clock. Skipped
            // rather than guessed at, because a scheduled send fires hours later
            // with nobody watching.
            const timezone = await resolveLocationTimezone(row.locationId)
            if (!timezone || !moment.tz.zone(timezone)) {
              record({ id: row.id, label, ok: false, skipped: true, reason: `This location has no timezone set` })
              continue
            }
            // The picked wall clock is validated against THIS practice's clock,
            // not the one the modal happened to show. A selection spanning New
            // York and Los Angeles at 7pm Pacific is 10pm Eastern, so "7:30 PM"
            // is a future time in one zone and a past one in the other. Without
            // this, the Eastern contact would be handed an instant already in the
            // past and the poller would dispatch it on its next sweep, while the
            // results modal said "scheduled for 7:30 PM". Skipped and reported,
            // the same as a missing timezone above.
            if (!isSchedulable(sendAtLocal, timezone, runStartedAt)) {
              record({
                id: row.id,
                label,
                ok: false,
                skipped: true,
                reason: `That time has already passed or falls in quiet hours at this practice`,
              })
              continue
            }
            if (queued.has(row.id)) {
              record({ id: row.id, label, ok: false, skipped: true, reason: `Already has a message scheduled` })
              continue
            }
            if (agentThreads.has(row.id)) {
              record({
                id: row.id,
                label,
                ok: false,
                skipped: true,
                reason: `The AI Agent is replying on this thread`,
              })
              continue
            }
            const sendDate = resolveSendAt(sendAtLocal, timezone).toISOString()
            try {
              const result = await gqlMutation(SEND_SMS_MUTATION, {
                input: {
                  contactId: row.id,
                  text: personalizeSms(smsValue.text, row, smsValue.fallbacks),
                  locationId: row.locationId,
                  sendDate,
                },
              })
              const ok = !result.error && !!result.data?.sendSms?.id
              if (!ok) {
                logError(`Could not schedule bulk SMS for contact ${row.id}`, result.error?.message ?? `Empty response`)
              }
              record({ id: row.id, label, ok, reason: ok ? undefined : `Could not be scheduled` })
            } catch (err) {
              logError(`Could not schedule bulk SMS for contact ${row.id}`, err)
              record({ id: row.id, label, ok: false, reason: `Could not be scheduled` })
            }
          }

          return {
            // `scheduledSendAtLocal` is what stops the results surface from
            // reporting a queued run as a completed send. Without it, 400 queued
            // texts read as "400 succeeded", meaning delivered. Present as long
            // as at least one contact was actually queued.
            scheduledSendAtLocal: tally.ok > 0 ? sendAtLocal : undefined,
            // The already-queued check is a fail-open safety net, not a hard
            // gate: a transient search error must not block a real send to
            // hundreds of contacts. But failing open silently would leave a
            // user with duplicated queued messages and no idea why, the same
            // failure mode this feature keeps having to guard against
            // elsewhere. Surfaced here rather than only logged.
            // Both checks are fail-open for the same reason, so both say so.
            // Joined rather than first-wins: they fail independently and a user
            // told about only one would act on a wrong picture of the run.
            caveat:
              [heldCheckFailed && ALREADY_QUEUED_CHECK_FAILED_CAVEAT, agentCheckFailed && AGENT_CHECK_FAILED_CAVEAT]
                .filter((text): text is string => !!text)
                .join(` `) || undefined,
            outcomes,
          }
        }
      }

      const outcomes: BulkRowOutcome[] = []
      let removedAny = false
      const smsSentPhones = new Set<string>()
      // Quiet-hours lookups, one per distinct location in the run. `undefined`
      // (location fetch failed, or no timezone on the record) still caches:
      // the check falls back to the viewer's clock either way.
      const locationTimezones = new Map<string, string | undefined>()
      const tally = { ok: 0, failed: 0, skipped: 0 }

      // Record a row's outcome and report progress. Each row is its own request, so
      // this fires per contact and drives a real progress bar rather than a guess.
      // This assumes the loop below stays sequential: `tally` and `outcomes.length`
      // are mutated and read with no synchronization, so batching rows through a
      // `Promise.all` (as the bulk API may want) needs a different counter here.
      const record = (outcome: BulkRowOutcome) => {
        outcomes.push(outcome)
        if (outcome.ok) tally.ok += 1
        else if (outcome.skipped) tally.skipped += 1
        else tally.failed += 1
        onProgress?.({ done: outcomes.length, total: rows.length, ...tally, last: outcome })
      }

      // A practice list takes the whole selection in one write, so its result is worked out here
      // and the loop below only reports each row's share of it, in the input row order.
      const listOutcomes = new Map<string, { ok: boolean; reason?: string }>()
      const { pipelineId: targetId, stage } = (actionId === PIPELINE_ACTION_ID ? value : {}) as PipelineValue
      const list =
        listsOn && targetId && !isSystemPipelineId(targetId)
          ? listStore.hits.find((candidate) => candidate.id === targetId)
          : undefined
      if (list) {
        // The rows the loop below would not refuse up front (no id, another workspace) are the ones written.
        const writable = rows.filter(
          (row) => row.id && !(currentWorkspaceId && row.workspaceId && row.workspaceId !== currentWorkspaceId),
        )
        try {
          const stageId = stageIdForName(list, stage)
          if (!stageId) throw new Error(`No stage ${stage} on list ${list.id}`)
          await membershipStore.load(writable.map((row) => row.id!))
          const onList = (row: ContactJson) =>
            membershipStore.membershipsOf(row.id!).some((membership) => membership.listId === list.id)
          for (const row of writable.filter(onList)) {
            listOutcomes.set(row.id!, { ok: false, reason: copy.alreadyInThisList })
          }
          const toAdd = writable.filter((row) => !onList(row))
          const { added } = toAdd.length
            ? await membershipStore.add(list.id, toAdd, stageId)
            : { added: [] as string[] }
          for (const row of toAdd) {
            listOutcomes.set(
              row.id!,
              added.includes(row.id!) ? { ok: true } : { ok: false, reason: `Unexpected error` },
            )
          }
        } catch (err) {
          logError(`Bulk add to list failed for list ${list.id}`, err)
          for (const row of writable.filter((candidate) => !listOutcomes.has(candidate.id!))) {
            listOutcomes.set(row.id!, { ok: false, reason: `Unexpected error` })
          }
        }
      } else if (listsOn && targetId && !isSystemPipelineId(targetId)) {
        // Neither a system board nor a known list: the pipeline write below would put a list id on the record.
        logError(`Bulk add to list found no list ${targetId}`)
        for (const row of rows.filter((candidate) => candidate.id)) {
          listOutcomes.set(row.id!, { ok: false, reason: `Unexpected error` })
        }
      }

      for (const row of rows) {
        const label = row.name || row.id || `Unknown contact`
        if (!row.id) {
          record({ id: ``, label, ok: false, reason: `Contact is missing an id` })
          continue
        }

        // The contact edit/message mutations are scoped to the active workspace;
        // a contact from another workspace (org-level view) would fail with a raw
        // "Access denied" — skip it up front with a readable reason.
        if (currentWorkspaceId && row.workspaceId && row.workspaceId !== currentWorkspaceId) {
          record({ id: row.id, label, ok: false, reason: `Contact is in another workspace` })
          continue
        }

        // Create-task is a create (not an update): make one task per contact.
        if (actionId === TASK_ACTION_ID) {
          const { title, dueDate, description, ownerId } = value as TaskValue
          if (!row.locationId) {
            record({ id: row.id, label, ok: false, reason: `Contact has no location` })
            continue
          }
          try {
            const res = await taskStore.create({
              title: substituteTokens(title, { contact: row }, { keepUnresolved: true }).text,
              dueDate: new Date(dueDate).toISOString(),
              contactId: row.id,
              locationId: row.locationId,
              ...(description
                ? { description: substituteTokens(description, { contact: row }, { keepUnresolved: true }).text }
                : {}),
              ...assigneeInput(ownerId || user?.id),
            })
            if (res.error) record({ id: row.id, label, ok: false, reason: res.error.message })
            else record({ id: row.id, label, ok: true })
          } catch (err) {
            logError(`Bulk create task failed for contact ${row.id}`, err)
            record({ id: row.id, label, ok: false, reason: `Unexpected error` })
          }
          continue
        }

        // Send SMS is a real outward message (not a data change): one per contact.
        // Deduplicate by phone+location so the same number doesn't receive the message twice
        // when multiple contacts share a cell phone.
        if (actionId === SMS_ACTION_ID) {
          const { text, fallbacks } = value as SmsValue
          if (!row.cellPhone) {
            record({ id: row.id, label, ok: false, skipped: true, reason: `No cell phone` })
            continue
          }
          if (!row.locationId) {
            record({ id: row.id, label, ok: false, reason: `No location` })
            continue
          }
          if (!locationTimezones.has(row.locationId)) {
            try {
              const location = await gqlFetchOne<LocationJson>(`location`, row.locationId)
              locationTimezones.set(row.locationId, location?.timezone ?? undefined)
            } catch (err) {
              logError(`Bulk send SMS could not load location ${row.locationId}`, err)
              locationTimezones.set(row.locationId, undefined)
            }
          }
          // Compliance quiet hours: 8:00 PM to 8:00 AM on the practice clock.
          // Bulk is business-initiated outreach by definition, so this is a
          // hard skip with no override (see the quiet-hours spec). Sits before
          // the phone dedup so a skipped row doesn't consume the phone key.
          if (isQuietHours(locationTimezones.get(row.locationId))) {
            record({ id: row.id, label, ok: false, skipped: true, reason: `Quiet hours at this practice (8pm-8am)` })
            continue
          }
          const phoneKey = `${row.cellPhone.replace(/\D/g, ``).slice(-10)}:${row.locationId}`
          if (smsSentPhones.has(phoneKey)) {
            record({ id: row.id, label, ok: false, skipped: true, reason: `Duplicate phone — already sent` })
            continue
          }
          smsSentPhones.add(phoneKey)
          try {
            const res = await gqlMutation(SEND_SMS_MUTATION, {
              input: {
                to: row.cellPhone,
                text: personalizeSms(text, row, fallbacks),
                locationId: row.locationId,
                contactId: row.id,
              },
            })
            if (res.error || !res.data?.sendSms?.id) {
              record({
                id: row.id,
                label,
                ...classifySendSmsFailure(res.error?.message ?? `Send failed`, res.error?.code),
              })
            } else {
              record({ id: row.id, label, ok: true })
            }
          } catch (err) {
            logError(`Bulk send SMS failed for contact ${row.id}`, err)
            record({ id: row.id, label, ok: false, reason: `Unexpected error` })
          }
          continue
        }

        // Enroll in workflow — a real enrollment: the workflow's steps run for this contact, which
        // can message them. Interim client-side adapter (one triggerWorkflowOnContact per row);
        // swapping to the engine's paced bulk API is the TRK-5383 handoff. The engine's own
        // re-enrollment policy applies per contact ("only once" contacts simply don't re-enroll).
        if (actionId === WORKFLOW_ACTION_ID) {
          const workflowId = value as string
          try {
            const res = await gqlMutation(TRIGGER_WORKFLOW_ON_CONTACT_MUTATION, { workflowId, contactId: row.id })
            if (res.error || !res.data?.triggerWorkflowOnContact?.success) {
              record({ id: row.id, label, ok: false, reason: res.error?.message ?? `Enrolment failed` })
            } else {
              record({ id: row.id, label, ok: true })
            }
          } catch (err) {
            logError(`Bulk workflow enrolment failed for contact ${row.id}`, err)
            record({ id: row.id, label, ok: false, reason: `Unexpected error` })
          }
          continue
        }

        // The membership store also leaves the tombstone that keeps a read inside the index's lag from
        // drawing the member back, on this table and on the list's board.
        if (actionId === REMOVE_FROM_LIST_ACTION_ID) {
          if (!removableList) {
            record({ id: row.id, label, ok: false, reason: `Unexpected error` })
            continue
          }
          try {
            await membershipStore.remove(removableList.id, row)
            removedAny = true
            record({ id: row.id, label, ok: true })
          } catch (err) {
            logError(`Bulk remove from list failed for contact ${row.id}`, err)
            record({ id: row.id, label, ok: false, reason: `Unexpected error` })
          }
          continue
        }

        // Add tags — dedicated server mutation (union).
        if (actionId === TAGS_ACTION_ID) {
          const tags = value as string[]
          try {
            const result = await contactStore.addTags(row.id, tags)
            if (result.error) record({ id: row.id, label, ok: false, reason: result.error.message })
            else record({ id: row.id, label, ok: true })
          } catch (err) {
            logError(`Bulk tags failed for contact ${row.id}`, err)
            record({ id: row.id, label, ok: false, reason: `Unexpected error` })
          }
          continue
        }

        // Add to pipeline — dedicated server mutation.
        if (actionId === PIPELINE_ACTION_ID) {
          const listOutcome = listOutcomes.get(row.id)
          if (listOutcome) {
            record({ id: row.id, label, ...listOutcome })
            continue
          }
          const { pipelineId, stage } = value as PipelineValue
          if ((row.pipelines ?? []).some((p) => p?.id === pipelineId)) {
            record({ id: row.id, label, ok: false, reason: copy.alreadyInThisList })
            continue
          }
          // A system board draws from its queue's own search, not from membership, so
          // a row the search does not match would get a membership no board can show
          // or clear. Refused per row rather than by hiding the pipeline outright: the
          // rest of the selection still goes through, and the results modal says which
          // ones did not and why.
          //
          // The flag is re-checked on the write, not just on the options above: the
          // selection and the toggle are read at different moments, and this is the
          // side that actually persists.
          const systemBoard = isSystemPipelineId(pipelineId)
          const mayWrite = systemBoard ? actionCenterEnabled && systemQueueAdmits(pipelineId, row) : true
          if (!mayWrite) {
            outcomes.push({
              id: row.id,
              label,
              ok: false,
              skipped: true,
              reason: `Not in this board's queue, so it could not show them`,
            })
            continue
          }
          try {
            const result = await contactStore.addToPipeline(row.id, { id: pipelineId, stage })
            if (result.error) {
              record({ id: row.id, label, ok: false, reason: result.error.message })
            } else {
              record({ id: row.id, label, ok: true })
              contactStore.addPendingPipelineContact(pipelineId, {
                ...row,
                pipelines: [...(row.pipelines ?? []), { id: pipelineId, stage }],
              } as ContactJson)
            }
          } catch (err) {
            logError(`Bulk add to pipeline failed for contact ${row.id}`, err)
            record({ id: row.id, label, ok: false, reason: `Unexpected error` })
          }
          continue
        }

        // Mark as non-patient — sends only `patientStatus`, exactly the drawer's payload.
        // NOTE: `UpdateContactInput` does not define `patientStatus` today, so this write
        // (like the drawer's) depends on the API accepting the field — an API-side change
        // owned outside this repo. Until it lands, the server rejects the row and the
        // error surfaces per-row in the results modal.
        if (actionId === NON_PATIENT_ACTION_ID) {
          const skipReason = nonPatientSkipReason(row)
          if (skipReason) {
            outcomes.push({ id: row.id, label, ok: false, skipped: true, reason: skipReason })
            continue
          }
          try {
            const result = await contactStore.update(row.id, { patientStatus: `NonPatient` })
            if (result.error) outcomes.push({ id: row.id, label, ok: false, reason: result.error.message })
            else outcomes.push({ id: row.id, label, ok: true })
          } catch (err) {
            logError(`Bulk mark non-patient failed for contact ${row.id}`, err)
            outcomes.push({ id: row.id, label, ok: false, reason: `Unexpected error` })
          }
          continue
        }

        // Closed Won is a terminal lead status the server locks from further edits.
        // The setter options already omit it as a target; guard the *source* side too
        // so changing an already-Closed-Won contact reports a readable reason instead
        // of the raw server rejection (mirrors the drawer's read-only Closed Won guard).
        if (actionId === `leadStatus` && row.leadStatus === `Closed Won`) {
          record({ id: row.id, label, ok: false, reason: `Lead status is locked once it's Closed Won` })
          continue
        }

        // Set-value fields (lead status, utm*, reported attribution) via updateContact.
        // The server also enforces field rules and returns a per-contact error, surfaced
        // in the results modal as a fallback for anything not pre-empted above.
        try {
          const field = (['leadStatus', 'utmSource', 'utmMedium', 'utmCampaign', 'reportedAttribution'] as const).find(
            (field) => field === actionId,
          )
          if (!field) throw new Error(`Unsupported contact field`)
          const input: UpdateContactInput = { [field]: value as string }
          const result = await contactStore.update(row.id, input)
          if (result.error) record({ id: row.id, label, ok: false, reason: result.error.message })
          else record({ id: row.id, label, ok: true })
        } catch (err) {
          logError(`Bulk action failed for contact ${row.id}`, err)
          record({ id: row.id, label, ok: false, reason: `Unexpected error` })
        }
      }

      // Once, after the run: re-reading the table while it is still running would shuffle the selection the
      // bar is reporting progress on.
      if (removableList && removedAny) await contactStore.refetchListMembers(removableList.id)

      return { outcomes }
    },
    [contactStore, taskStore, listStore, membershipStore, listsOn, user?.id, currentWorkspaceId, copy, removableList],
  )

  return { actions, apply }
}
